import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  getConsent,
  setConsent,
  track,
  COOKIE_SETTINGS_EVENT,
} from "@/lib/consent";

export const CookieBanner = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!getConsent()) setOpen(true);
    const reopen = () => setOpen(true);
    window.addEventListener(COOKIE_SETTINGS_EVENT, reopen);
    return () => window.removeEventListener(COOKIE_SETTINGS_EVENT, reopen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The cookie banner isn't required inside the authenticated admin area, and
  // it would otherwise overlap admin controls. Hide it there.
  if (window.location.pathname.startsWith("/admin")) return null;

  const accept = () => {
    setConsent("accepted");
    setOpen(false);
    track("pageview"); // first tracked view once consent is granted
  };

  const reject = () => {
    setConsent("rejected");
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-4 sm:px-6 sm:pb-6 pointer-events-none"
          data-testid="cookie-banner"
        >
          <div className="mx-auto max-w-3xl rounded-2xl border border-border bg-white shadow-[0_12px_48px_rgba(17,42,34,0.12)] p-6 sm:p-7 pointer-events-auto">
            <div className="flex items-start gap-3">
              <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-clay/10 text-clay">
                <Cookie className="h-5 w-5" strokeWidth={2} />
              </div>
              <div className="text-sm text-ink-soft leading-relaxed">
                <p className="font-heading text-base text-ink">We use cookies</p>
                <p className="mt-1">
                  We use necessary cookies to run the site and analytics cookies
                  to understand how it's used. Analytics cookies are only set if
                  you accept. Read more in our{" "}
                  <Link to="/privacy" className="text-clay underline underline-offset-2">
                    Privacy Policy
                  </Link>
                  .
                </p>
              </div>
            </div>
            <div className="mt-5 flex flex-col sm:flex-row gap-3 sm:justify-end">
              <Button
                data-testid="cookie-reject"
                onClick={reject}
                variant="outline"
                className="border-border text-ink hover:bg-sand-deep rounded-lg px-6 py-5 font-medium order-2 sm:order-1"
              >
                Necessary only
              </Button>
              <Button
                data-testid="cookie-accept"
                onClick={accept}
                className="bg-clay hover:bg-clay-hover text-white rounded-lg px-6 py-5 font-medium order-1 sm:order-2"
              >
                Accept
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const RouteTracker = ({ pathname }) => {
  useEffect(() => {
    track("pageview", pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);
  return null;
};
